import { useState, useRef } from 'react';
import '../styles/histogram.css';

const SingleHistogram = ({ data, stats, label, colorScheme = 'default' }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const containerRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <div className="histogram-empty">
        <p>No data available</p>
      </div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 1);
  const totalCount = data.reduce((sum, d) => sum + d.count, 0);

  // Color scale based on response time
  const getBarColor = (min) => {
    if (colorScheme === 'blue') {
      // Blue color scheme for OS/Endpoint B
      if (min < 100) return '#22d3ee'; // cyan
      if (min < 500) return '#3b82f6'; // blue
      if (min < 1000) return '#6366f1'; // indigo
      if (min < 2500) return '#8b5cf6'; // violet
      return '#a855f7'; // purple
    }
    // Green/yellow/red for PG/Endpoint A (default)
    if (min < 100) return 'var(--success)';
    if (min < 500) return 'var(--success-muted, #4ade80)';
    if (min < 1000) return 'var(--warning, #fbbf24)';
    if (min < 2500) return 'var(--warning-intense, #f97316)';
    return 'var(--danger)';
  };

  const handleBarHover = (e, bucket) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const percentage = totalCount > 0 ? ((bucket.count / totalCount) * 100).toFixed(1) : 0;
    
    setTooltip({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 10,
      content: `${bucket.label}: ${bucket.count} requests (${percentage}%)`
    });
  };

  const handleBarLeave = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  return (
    <div className="single-histogram" ref={containerRef}>
      {/* Label and stats row */}
      <div className="histogram-header">
        <span className={`histogram-label ${colorScheme}`}>{label}</span>
        {stats && (
          <div className="histogram-stats-inline">
            <span className="stat-inline">
              <span className="stat-key">P50:</span> {stats.p50 ? `${stats.p50}ms` : '-'}
            </span>
            <span className="stat-inline">
              <span className="stat-key">P90:</span> {stats.p90 ? `${stats.p90}ms` : '-'}
            </span>
            <span className="stat-inline">
              <span className="stat-key">P95:</span> {stats.p95 ? `${stats.p95}ms` : '-'}
            </span>
            <span className="stat-inline">
              <span className="stat-key">P99:</span> {stats.p99 ? `${stats.p99}ms` : '-'}
            </span>
            <span className="stat-inline total">
              <span className="stat-key">Total:</span> {stats.totalRequests || 0}
            </span>
          </div>
        )}
      </div>

      {/* Histogram bars */}
      <div className="histogram-bars">
        {data.map((bucket) => {
          const height = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
          const hasData = bucket.count > 0;
          
          return (
            <div 
              key={bucket.label} 
              className="histogram-bar-container"
              onMouseMove={(e) => handleBarHover(e, bucket)}
              onMouseLeave={handleBarLeave}
            >
              <div className="histogram-bar-wrapper">
                <div 
                  className={`histogram-bar ${hasData ? 'has-data' : ''}`}
                  style={{ 
                    height: `${Math.max(height, hasData ? 2 : 0)}%`,
                    backgroundColor: getBarColor(bucket.min),
                    opacity: hasData ? 1 : 0.15
                  }}
                >
                  {hasData && height > 20 && (
                    <span className="bar-count">{bucket.count}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div 
          className="histogram-tooltip"
          style={{ 
            left: tooltip.x, 
            top: tooltip.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

const ResponseTimeHistogram = ({ data }) => {
  if (!data) {
    return (
      <div className="histogram-empty">
        <p>No response time data available</p>
        <span className="empty-hint">Run some tests to see the distribution</span>
      </div>
    );
  }

  const { endpointA, endpointB } = data;
  const hasEndpointAData = endpointA?.stats?.totalRequests > 0;
  const hasEndpointBData = endpointB?.stats?.totalRequests > 0;

  if (!hasEndpointAData && !hasEndpointBData) {
    return (
      <div className="histogram-empty">
        <p>No response time data available</p>
        <span className="empty-hint">Run some tests to see the distribution</span>
      </div>
    );
  }

  return (
    <div className="histogram-container dual">
      {/* Endpoint A - PG */}
      <SingleHistogram 
        data={endpointA?.distribution} 
        stats={endpointA?.stats}
        label={endpointA?.label || 'PG'}
        colorScheme="default"
      />

      {/* X-axis labels (shared) */}
      <div className="histogram-x-axis">
        {endpointA?.distribution?.map((bucket) => (
          <div key={bucket.label} className="x-axis-label">
            <span className="x-label-text">
              {bucket.label.replace('ms', '').replace('-', '-').split('-')[0]}
            </span>
          </div>
        ))}
      </div>

      {/* Endpoint B - OS */}
      {hasEndpointBData && (
        <SingleHistogram 
          data={endpointB?.distribution} 
          stats={endpointB?.stats}
          label={endpointB?.label || 'OS'}
          colorScheme="blue"
        />
      )}

      {/* Scale indicator */}
      <div className="histogram-scale-indicator">
        <span className="scale-label">Response Time (log scale) →</span>
      </div>
    </div>
  );
};

export default ResponseTimeHistogram;
